import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel, FailureNotice, Panel } from '@/components/platform/AsyncPanel';
import { InstitutionPicker } from '@/components/platform/InstitutionPicker';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import { platformApi } from '@/api/platform';
import { useAsyncData } from '@/hooks';
import {
  FEATURE_FLAG_SCOPES,
  FLAG_SCOPE_PRECEDENCE,
  type FeatureDefinitionSummary,
  type FeatureFlagScope,
  type FeatureFlagSetting,
  type FeatureFlagVersion,
  type ResolvedFeatureFlag,
  type SetFeatureFlagRequest,
} from '@/types/platform';
import { formatDateTime } from '@/utils/format';
import styles from './platform-pages.module.css';

/**
 * PA-05 — feature configuration per institution, campus, role and academic year.
 *
 * PRECEDENCE IS SHOWN, NOT DESCRIBED.
 * Four scopes with an ordering is exactly the kind of rule people believe they
 * understand and get backwards. The order rendered on this page is the same array
 * the resolver walks, so a change to the contract changes the screen. And rather
 * than asking the operator to apply it in their head, the resolver itself is
 * exposed: type a campus, a role and an academic year, and the server says which
 * setting actually wins in that context and which scope it came from.
 *
 * WARNINGS COME BEFORE THE SUBMISSION, NOT AFTER IT.
 * The server refuses a change that breaks a dependency or turns on two
 * incompatible features, with a 409. Discovering that after committing to the
 * change teaches nothing about what the dependency was. So the feature's declared
 * `requires` and `conflictsWith` are fetched and shown while the form is open —
 * and when that catalogue is unavailable the form says the rules could not be
 * checked, which is a different statement from "there are no rules".
 */

export function FeatureFlagsPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [params, setParams] = useSearchParams();
  const institutionId = params.get('institutionId') ?? '';

  const fetcher = useCallback(() => platformApi.featureFlags.list(institutionId), [institutionId]);
  const { state, reload } = useAsyncData<FeatureFlagSetting[]>(
    `feature-flags:${institutionId}`,
    fetcher,
    institutionId.length > 0,
  );

  const [editing, setEditing] = useState<FeatureFlagSetting | 'new' | null>(null);
  const [historyFor, setHistoryFor] = useState<string | null>(null);

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{t('platform.flags.title')}</h1>
          <p className={styles.pageLead}>{t('platform.flags.lead')}</p>
        </div>
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={() => setEditing('new')}
          disabled={institutionId.length === 0}
        >
          <Icon name="plus" size={16} />
          {t('platform.flags.set')}
        </button>
      </header>

      <PrecedenceExplainer />

      <div className={styles.filterBar} role="search">
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="flags-institution">
            {t('platform.flags.institutionLabel')}
          </label>
          {/* Picking applies immediately — no UUID to paste, no submit to press. */}
          <InstitutionPicker
            id="flags-institution"
            value={institutionId}
            clearable
            onChange={(next) => setParams(next ? { institutionId: next } : {})}
          />
        </div>
      </div>

      {institutionId.length === 0 ? (
        <p className={styles.sectionEmpty} role="status">
          {t('platform.flags.chooseInstitution')}
        </p>
      ) : (
        <>
          <ResolverPanel institutionId={institutionId} />

          <Panel title={t('platform.flags.settingsTitle')} icon="checklist">
            <AsyncPanel
              state={state}
              onRetry={reload}
              isEmpty={(data) => data.length === 0}
              emptyTitle={t('platform.flags.emptyTitle')}
              emptyBody={t('platform.flags.emptyBody')}
            >
              {(data) => <SettingsTable settings={data} locale={locale} onEdit={setEditing} onHistory={setHistoryFor} />}
            </AsyncPanel>
          </Panel>
        </>
      )}

      <SetFlagDialog
        target={editing}
        institutionId={institutionId}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          reload();
        }}
      />

      <VersionHistoryDialog
        institutionId={institutionId}
        featureCode={historyFor}
        onClose={() => setHistoryFor(null)}
        locale={locale}
      />
    </div>
  );
}

/** The resolution order, rendered from the array the resolver actually walks. */
function PrecedenceExplainer() {
  const { t } = useTranslation();

  return (
    <Panel title={t('platform.flags.precedenceTitle')} icon="info">
      <ol className={styles.precedence}>
        {FLAG_SCOPE_PRECEDENCE.map((scope, index) => (
          <li key={scope} className={styles.precedenceItem}>
            <span className={styles.precedenceRank}>{index + 1}</span>
            <span>
              <strong>{t(`platform.flags.scope.${scope}`)}</strong>
              <span className={styles.fieldHint}>{t(`platform.flags.scopeHint.${scope}`)}</span>
            </span>
          </li>
        ))}
      </ol>
      <p className={styles.fieldHint}>{t('platform.flags.precedenceNote')}</p>
    </Panel>
  );
}

/* ─── Resolver ─── */

/**
 * Asks the server what actually applies in a context.
 *
 * This exists because the alternative is the operator reading a table of six
 * settings across four scopes and working out the answer by hand — which is the
 * step where the mistake happens, and the mistake is invisible because the wrong
 * answer is still a plausible one.
 */
function ResolverPanel({ institutionId }: { institutionId: string }) {
  const { t } = useTranslation();
  const [featureCode, setFeatureCode] = useState('');
  const [campusId, setCampusId] = useState('');
  const [role, setRole] = useState('');
  const [academicYearId, setAcademicYearId] = useState('');
  const [submitted, setSubmitted] = useState<{
    featureCode: string;
    campusId: string;
    role: string;
    academicYearId: string;
  } | null>(null);

  const key = submitted
    ? `flag-resolve:${institutionId}:${submitted.featureCode}:${submitted.campusId}:${submitted.role}:${submitted.academicYearId}`
    : 'flag-resolve:idle';

  const fetcher = useCallback(
    () =>
      platformApi.featureFlags.resolve(institutionId, submitted?.featureCode ?? '', {
        campusId: submitted?.campusId || undefined,
        role: submitted?.role || undefined,
        academicYearId: submitted?.academicYearId || undefined,
      }),
    [institutionId, submitted],
  );

  const { state, reload } = useAsyncData<ResolvedFeatureFlag>(key, fetcher, submitted !== null);

  return (
    <Panel title={t('platform.flags.resolverTitle')} icon="pulse">
      <p className={styles.fieldHint}>{t('platform.flags.resolverLead')}</p>
      <form
        className={styles.filterGrid}
        onSubmit={(event) => {
          event.preventDefault();
          if (featureCode.trim().length === 0) return;
          setSubmitted({
            featureCode: featureCode.trim(),
            campusId: campusId.trim(),
            role: role.trim(),
            academicYearId: academicYearId.trim(),
          });
        }}
      >
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="resolve-feature">
            {t('platform.flags.fieldFeature')}
          </label>
          <input
            id="resolve-feature"
            className={styles.input}
            value={featureCode}
            required
            onChange={(event) => setFeatureCode(event.target.value.toUpperCase())}
          />
        </div>
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="resolve-campus">
            {t('platform.flags.scope.CAMPUS')}
          </label>
          <input id="resolve-campus" className={styles.input} value={campusId} onChange={(event) => setCampusId(event.target.value)} />
        </div>
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="resolve-role">
            {t('platform.flags.scope.ROLE')}
          </label>
          <input id="resolve-role" className={styles.input} value={role} onChange={(event) => setRole(event.target.value)} />
        </div>
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="resolve-year">
            {t('platform.flags.scope.ACADEMIC_YEAR')}
          </label>
          <input
            id="resolve-year"
            className={styles.input}
            value={academicYearId}
            onChange={(event) => setAcademicYearId(event.target.value)}
          />
        </div>
        <div className={styles.filterActions}>
          <button type="submit" className={styles.primaryBtnSm} disabled={featureCode.trim().length === 0}>
            {t('platform.flags.resolve')}
          </button>
        </div>
      </form>

      {submitted && (
        <AsyncPanel state={state} onRetry={reload}>
          {(resolved) => (
            <dl className={styles.detailGrid}>
              <dt>{t('platform.flags.effective')}</dt>
              <dd>
                <span className={resolved.enabled ? styles.okTag : styles.mutedTag}>
                  {resolved.enabled ? t('platform.flags.enabled') : t('platform.flags.disabled')}
                </span>
              </dd>
              <dt>{t('platform.flags.resolvedFrom')}</dt>
              <dd>
                {resolved.resolvedFromScope
                  ? `${t(`platform.flags.scope.${resolved.resolvedFromScope}`)}${
                      resolved.resolvedFromScopeValue ? ` · ${resolved.resolvedFromScopeValue}` : ''
                    }`
                  : t('platform.flags.noSettingAnywhere')}
              </dd>
              <dt>{t('platform.flags.version')}</dt>
              <dd>{resolved.version ?? '—'}</dd>
            </dl>
          )}
        </AsyncPanel>
      )}
    </Panel>
  );
}

/* ─── Settings table ─── */

function SettingsTable({
  settings,
  locale,
  onEdit,
  onHistory,
}: {
  settings: FeatureFlagSetting[];
  locale: string;
  onEdit: (setting: FeatureFlagSetting) => void;
  onHistory: (featureCode: string) => void;
}) {
  const { t } = useTranslation();

  // Grouped by feature so all four scopes for one flag sit together: the question
  // being asked is always "what is the state of THIS feature", never "show me
  // every campus-scoped setting".
  const grouped = useMemo(() => {
    const map = new Map<string, FeatureFlagSetting[]>();
    for (const setting of settings) {
      const list = map.get(setting.featureCode) ?? [];
      list.push(setting);
      map.set(setting.featureCode, list);
    }
    for (const list of map.values()) {
      list.sort(
        (a, b) => FLAG_SCOPE_PRECEDENCE.indexOf(a.scope) - FLAG_SCOPE_PRECEDENCE.indexOf(b.scope),
      );
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [settings]);

  return (
    <div className={styles.flagGroups}>
      {grouped.map(([featureCode, rows]) => (
        <section key={featureCode} className={styles.flagGroup}>
          <header className={styles.flagGroupHeader}>
            <h3 className={styles.subheading}>{featureCode}</h3>
            <button type="button" className={styles.linkBtn} onClick={() => onHistory(featureCode)}>
              {t('platform.flags.history')}
            </button>
          </header>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <caption className={styles.tableCaption}>
                {t('platform.flags.groupCaption', { feature: featureCode, n: rows.length })}
              </caption>
              <thead>
                <tr>
                  <th scope="col">{t('platform.flags.colScope')}</th>
                  <th scope="col">{t('platform.flags.colScopeValue')}</th>
                  <th scope="col">{t('platform.flags.colState')}</th>
                  <th scope="col">{t('platform.flags.colVersion')}</th>
                  <th scope="col">{t('platform.flags.colChanged')}</th>
                  <th scope="col">
                    <span className={styles.srOnlyText}>{t('platform.flags.colActions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((setting) => (
                  <tr key={setting.id}>
                    <th scope="row" className={styles.tableRowHeader}>
                      {t(`platform.flags.scope.${setting.scope}`, { defaultValue: setting.scope })}
                      <span className={styles.tableSub}>
                        {t('platform.flags.rank', {
                          rank: FLAG_SCOPE_PRECEDENCE.indexOf(setting.scope) + 1,
                        })}
                      </span>
                    </th>
                    <td className={setting.scopeValue ? undefined : styles.mutedCell}>
                      {setting.scopeValue || t('platform.flags.wholeInstitution')}
                    </td>
                    <td>
                      <span className={setting.enabled ? styles.okTag : styles.mutedTag}>
                        {setting.enabled ? t('platform.flags.enabled') : t('platform.flags.disabled')}
                      </span>
                    </td>
                    <td className={styles.mutedCell}>{setting.version}</td>
                    <td className={styles.mutedCell}>
                      {formatDateTime(setting.updatedAt, locale)}
                      {setting.updatedBy ? ` · ${setting.updatedBy}` : ''}
                    </td>
                    <td>
                      <button type="button" className={styles.linkBtn} onClick={() => onEdit(setting)}>
                        {t('platform.flags.change')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
}

/* ─── Set ─── */

const MIN_FLAG_REASON = 3;

function SetFlagDialog({
  target,
  institutionId,
  onClose,
  onSaved,
}: {
  target: FeatureFlagSetting | 'new' | null;
  institutionId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<SetFeatureFlagRequest>(emptyFlag());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!target) return;
    setError(null);
    setForm(
      target === 'new'
        ? emptyFlag()
        : {
            featureCode: target.featureCode,
            scope: target.scope,
            scopeValue: target.scopeValue,
            // Pre-set to the OPPOSITE of the current value: this dialog is opened
            // to change a flag, and pre-filling the current state means a
            // distracted operator can submit a no-op change with a reason attached
            // and believe they turned something off.
            enabled: !target.enabled,
            reason: '',
          },
    );
  }, [target]);

  const { state: catalogueState } = useAsyncData<FeatureDefinitionSummary[]>(
    'feature-catalogue',
    () => platformApi.featureFlags.catalogue(),
    target !== null,
  );

  const definition =
    catalogueState.status === 'ready'
      ? catalogueState.data.find((entry) => entry.code === form.featureCode)
      : undefined;

  const scopeNeedsValue = form.scope !== 'INSTITUTION';
  const valid =
    form.featureCode.trim().length > 0 &&
    (!scopeNeedsValue || (form.scopeValue ?? '').trim().length > 0) &&
    form.reason.trim().length >= MIN_FLAG_REASON;

  if (!target) return null;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await platformApi.featureFlags.set(institutionId, {
        featureCode: form.featureCode.trim(),
        scope: form.scope,
        // The server ignores a scope value on INSTITUTION scope; sending one
        // anyway would make the request look like it did something it did not.
        scopeValue: scopeNeedsValue ? form.scopeValue?.trim() : undefined,
        enabled: form.enabled,
        reason: form.reason.trim(),
      });
      onSaved();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t('platform.flags.setTitle')}
      description={t('platform.flags.setLead')}
      closeOnBackdropClick={false}
    >
      <form className={styles.dialogForm} onSubmit={submit} noValidate>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.flags.fieldFeature')}</span>
          <input
            className={styles.input}
            value={form.featureCode}
            required
            readOnly={target !== 'new'}
            onChange={(event) => setForm({ ...form, featureCode: event.target.value.toUpperCase() })}
          />
        </label>

        <div className={styles.dialogRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.flags.colScope')}</span>
            <Select
              value={form.scope}
              onChange={(value) =>
                setForm({ ...form, scope: value as FeatureFlagScope, scopeValue: '' })
              }
              options={FEATURE_FLAG_SCOPES.map((scope) => ({
                value: scope,
                label: t(`platform.flags.scope.${scope}`),
              }))}
            />
            <span className={styles.fieldHint}>
              {t('platform.flags.rank', { rank: FLAG_SCOPE_PRECEDENCE.indexOf(form.scope) + 1 })}
            </span>
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.flags.colScopeValue')}</span>
            <input
              className={styles.input}
              value={form.scopeValue ?? ''}
              disabled={!scopeNeedsValue}
              required={scopeNeedsValue}
              onChange={(event) => setForm({ ...form, scopeValue: event.target.value })}
            />
            <span className={styles.fieldHint}>
              {scopeNeedsValue
                ? t(`platform.flags.scopeValueHint.${form.scope}`)
                : t('platform.flags.scopeValueNone')}
            </span>
          </label>
        </div>

        <Checkbox
          label={t('platform.flags.enabledLabel')}
          checked={form.enabled}
          onChange={(event) => setForm({ ...form, enabled: event.target.checked })}
        />

        <DependencyWarnings
          definition={definition}
          enabled={form.enabled}
          unavailable={catalogueState.status === 'error'}
          error={catalogueState.status === 'error' ? catalogueState.error : undefined}
        />

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.confirm.reasonLabel')}</span>
          <textarea
            className={styles.textarea}
            rows={3}
            value={form.reason}
            required
            aria-invalid={form.reason.length > 0 && form.reason.trim().length < MIN_FLAG_REASON}
            onChange={(event) => setForm({ ...form, reason: event.target.value })}
          />
          <span className={styles.fieldHint}>{t('platform.flags.reasonHint')}</span>
        </label>

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button type="button" className="modal-button modal-button--secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="modal-button" disabled={!valid || submitting}>
            {submitting ? t('platform.confirm.submitting') : t('platform.flags.save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function emptyFlag(): SetFeatureFlagRequest {
  return { featureCode: '', scope: 'INSTITUTION', scopeValue: '', enabled: true, reason: '' };
}

/**
 * What this change would break, shown before it is made.
 *
 * The FOUR states are deliberately distinct. "This feature declares no
 * dependencies" is a fact. "The catalogue could not be read" is an unknown, and
 * saying nothing in that case would be indistinguishable from the fact — which is
 * how an operator ends up confident that a change is safe because a request
 * silently 404d.
 *
 * The fourth is `rulesReadable: false`, and it is the one that was missing. The
 * catalogue answered, but THIS feature's metadata could not be parsed, so
 * `requires` and `conflictsWith` arrive empty for a reason that has nothing to do
 * with the feature having no dependencies. Core sends the flag precisely so a
 * client cannot collapse the two; rendering it as "no dependencies" would be the
 * most confident wrong answer on this screen.
 */
function DependencyWarnings({
  definition,
  enabled,
  unavailable,
  error,
}: {
  definition: FeatureDefinitionSummary | undefined;
  enabled: boolean;
  unavailable: boolean;
  error?: import('@/api/client').ApiError;
}) {
  const { t } = useTranslation();

  if (unavailable && error) {
    return (
      <div className={styles.warningNotice} role="note">
        <Icon name="warning" size={16} aria-hidden="true" />
        <div>
          <p className={styles.warningTitle}>{t('platform.flags.rulesUncheckedTitle')}</p>
          <p className={styles.warningBody}>{t('platform.flags.rulesUncheckedBody')}</p>
          <FailureNotice error={error} />
        </div>
      </div>
    );
  }

  if (!definition) {
    return <p className={styles.fieldHint}>{t('platform.flags.noDefinition')}</p>;
  }

  if (definition.rulesReadable === false) {
    return (
      <div className={styles.warningNotice} role="note">
        <Icon name="warning" size={16} aria-hidden="true" />
        <div>
          <p className={styles.warningTitle}>{t('platform.flags.rulesUnreadableTitle')}</p>
          <p className={styles.warningBody}>{t('platform.flags.rulesUnreadableBody')}</p>
          {definition.rulesError && <p className={styles.rowDetailError}>{definition.rulesError}</p>}
        </div>
      </div>
    );
  }

  const requires = definition.requires ?? [];
  const conflicts = definition.conflictsWith ?? [];

  if (requires.length === 0 && conflicts.length === 0) {
    return <p className={styles.fieldHint}>{t('platform.flags.noDependencies')}</p>;
  }

  return (
    <div className={styles.warningNotice} role="note">
      <Icon name="warning" size={16} aria-hidden="true" />
      <div>
        <p className={styles.warningTitle}>{t('platform.flags.dependencyTitle')}</p>
        {enabled && requires.length > 0 && (
          <p className={styles.warningBody}>{t('platform.flags.requires', { codes: requires.join(', ') })}</p>
        )}
        {enabled && conflicts.length > 0 && (
          <p className={styles.warningBody}>{t('platform.flags.conflicts', { codes: conflicts.join(', ') })}</p>
        )}
        {!enabled && requires.length > 0 && (
          <p className={styles.warningBody}>{t('platform.flags.disablingDependents')}</p>
        )}
        <p className={styles.warningBody}>{t('platform.flags.serverDecides')}</p>
      </div>
    </div>
  );
}

/* ─── History ─── */

function VersionHistoryDialog({
  institutionId,
  featureCode,
  onClose,
  locale,
}: {
  institutionId: string;
  featureCode: string | null;
  onClose: () => void;
  locale: string;
}) {
  const { t } = useTranslation();
  const code = featureCode ?? '';
  const fetcher = useCallback(
    () => platformApi.featureFlags.versions(institutionId, code),
    [institutionId, code],
  );
  const { state, reload } = useAsyncData<FeatureFlagVersion[]>(
    `flag-versions:${institutionId}:${code}`,
    fetcher,
    code.length > 0,
  );

  if (!featureCode) return null;

  return (
    <Modal open onClose={onClose} title={t('platform.flags.historyTitle', { feature: featureCode })} description={t('platform.flags.historyLead')}>
      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={(data) => data.length === 0}
        emptyTitle={t('platform.flags.historyEmpty')}
      >
        {(versions) => (
          <ol className={styles.timeline}>
            {versions.map((version) => (
              <li key={version.id} className={styles.timelineItem}>
                <span className={styles.timelineDot} aria-hidden="true" />
                <div>
                  <p className={styles.rowTitle}>
                    {t('platform.flags.versionLine', {
                      version: version.version,
                      state: version.enabled ? t('platform.flags.enabled') : t('platform.flags.disabled'),
                      scope: t(`platform.flags.scope.${version.scope}`, { defaultValue: version.scope }),
                    })}
                  </p>
                  <p className={styles.rowMetaLine}>
                    {formatDateTime(version.changedAt ?? version.createdAt, locale)}
                    {version.changedBy ? ` · ${version.changedBy}` : ''}
                    {version.scopeValue ? ` · ${version.scopeValue}` : ''}
                  </p>
                  {version.reason && <p className={styles.rowDetail}>{version.reason}</p>}
                </div>
              </li>
            ))}
          </ol>
        )}
      </AsyncPanel>

      <div className="modal-actions">
        <button type="button" className="modal-button" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
    </Modal>
  );
}
